import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regCompany, setRegCompany] = useState("");
  const [regSiret, setRegSiret] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = (user.role === "admin" || user.role === "webmaster") ? "/dashboard" : "/espace-client";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  if (user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginEmail, loginPassword);
      navigate((u.role === "admin" || u.role === "webmaster") ? "/dashboard" : "/espace-client");
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regPassword.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères"); return; }
    if (!regCompany && !regSiret) { toast.error("Veuillez renseigner le nom de votre société ou votre SIRET"); return; }
    setLoading(true);
    try {
      const u = await register(regName, regEmail, regPassword, regCompany, regSiret);
      navigate((u.role === "admin" || u.role === "webmaster") ? "/dashboard" : "/espace-client");
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const GoogleButton = ({ text }) => (
    <Button variant="outline" className="w-full h-11 border-slate-200" onClick={loginWithGoogle} data-testid="google-auth-btn">
      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
      {text}
    </Button>
  );

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-[400px]">
          <div className="flex items-center gap-3 mb-10">
            <img src="/jvc-logo.png" alt="JVC" className="w-14 h-14 rounded-md object-contain" />
            <div>
              <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">SAS J.V.C</h1>
              <p className="text-xs text-slate-500">Structure métallique, Soudure industrielle, Serrurerie</p>
            </div>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Espace Client</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email" className="text-slate-700">Email</Label>
                  <Input id="login-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="email@exemple.com" required data-testid="login-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password" className="text-slate-700">Mot de passe</Label>
                  <Input id="login-password" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Votre mot de passe" required data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-slate-400">ou</span></div>
              </div>
              <GoogleButton text="Se connecter avec Google" />
            </TabsContent>

            <TabsContent value="register">
              <p className="text-xs text-slate-500 mb-4">Créez votre compte client pour accéder à vos devis, commandes et factures.</p>
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-name" className="text-slate-700">Nom complet</Label>
                  <Input id="reg-name" value={regName} onChange={e => setRegName(e.target.value)} placeholder="Jean Dupont" required data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email" className="text-slate-700">Email</Label>
                  <Input id="reg-email" type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="email@exemple.com" required data-testid="register-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-company" className="text-slate-700">Nom de la société</Label>
                  <Input id="reg-company" value={regCompany} onChange={e => setRegCompany(e.target.value)} placeholder="Ma Société SAS" data-testid="register-company-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-siret" className="text-slate-700">SIRET <span className="text-slate-400 font-normal">(ou email identique au devis)</span></Label>
                  <Input id="reg-siret" value={regSiret} onChange={e => setRegSiret(e.target.value)} placeholder="123 456 789 00012" data-testid="register-siret-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password" className="text-slate-700">Mot de passe</Label>
                  <Input id="reg-password" type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Minimum 6 caractères" required data-testid="register-password-input" />
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white" disabled={loading} data-testid="register-submit-btn">
                  {loading ? "Inscription..." : "Créer mon espace client"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-[9px] text-slate-300 text-center mt-8">CopyRight by De La Rosa Design</p>
        </div>
      </div>

      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1745448797901-2a4c9d9af1c1?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Soudure industrielle"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1a3a5c]/80 flex items-end p-12">
          <div>
            <img src="/jvc-logo.png" alt="JVC" className="w-20 h-20 rounded-lg object-contain bg-white/10 p-1 mb-4" />
            <h2 className="font-barlow text-4xl font-bold text-white tracking-tight uppercase mb-2">SAS J.V.C</h2>
            <p className="text-blue-200 text-base max-w-md leading-relaxed">
              Structure métallique, Soudure industrielle, Serrurerie. Gestion complète de vos devis et bons de commande.
            </p>
            <div className="flex gap-6 mt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-400 font-barlow">97310</p>
                <p className="text-xs text-blue-300">Kourou</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-400 font-barlow">33.20A</p>
                <p className="text-xs text-blue-300">Code APE</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
